import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';

export const COLOR_PRESETS = [
  '#3B82F6',
  '#22C55E',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F97316'
];

export function StageFormDialog({
  open,
  onOpenChange,
  title,
  defaultName,
  defaultColor,
  onSubmit,
  isPending,
  error
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  defaultName?: string;
  defaultColor?: string;
  onSubmit: (name: string, color: string) => void;
  isPending?: boolean;
  error?: string | null;
}): React.ReactNode {
  const { t } = useTranslation();
  const [name, setName] = useState(defaultName ?? '');
  const [color, setColor] = useState(defaultColor ?? COLOR_PRESETS[0] ?? '#3B82F6');

  useEffect(() => {
    if (open) {
      setName(defaultName ?? '');
      setColor(defaultColor ?? COLOR_PRESETS[0] ?? '#3B82F6');
    }
  }, [open, defaultName, defaultColor]);

  function handleSubmit(e: { preventDefault: () => void }): void {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit(name.trim(), color);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="stage-name">
              {t('workflows.stages.stageName')}
            </label>
            <Input
              id="stage-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="e.g. Phone Screen"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              {t('workflows.stages.stageColor')}
            </label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setColor(c);
                  }}
                  className={`size-8 rounded-full border-2 transition-transform ${
                    color === c ? 'scale-110 border-foreground' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="submit" disabled={isPending || !name.trim()}>
              {t('actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
